import { EmailField } from '@flos/react-ui';
import { useEffect, useState } from 'react';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { isRequired } from '../../helpers/isRequired';
import { SummaryRowTemplate } from '../../helpers/templates';
import { getPrimaryEmail } from '../../helpers/topContext';
import useAppState from '../../state/appState';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { ElementValue, ElementValueHuman } from '../../types/element-values';
import { ContactEmail as ContactEmailType, FormConfig, FormValues } from '@topdanmark-onlinesolutions/claim-form-types';
import { getIsValid, getValidationErrors } from '../../validators/validator-mapping';
import { getValue } from '../values/Get';

const ContactEmail = ({ caption, placeholder, elementId }: ContactEmailType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const valueIsDefinedInState = formValues[elementId] === undefined ? false : true;

    const value = ContactEmail.getValueOrDefault(formValues, elementId);

    useEffect(() => {
        if (!valueIsDefinedInState) {
            setFormValue(elementId, value);
        }
    }, [valueIsDefinedInState, setFormValue, elementId, value]);

    const [errorMessage, setErrorMessage] = useState<string>('');
    const [fieldHasHadFocusAtLeastOnce, setFieldHasHadFocusAtLeastOnce] = useState(false);

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        if (!fieldHasHadFocusAtLeastOnce) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    const setValue = (value: string) => {
        setFormValue(elementId, value);
    };

    const isRequiredField = isRequired(formConfig, elementId);
    const isValid = ContactEmail.isValidField(formConfig, formValues, elementId, fieldHasHadFocusAtLeastOnce);

    return (
        <>
            {caption && <h6>{caption}</h6>}
            <EmailField
                value={value}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const value = event.target.value;
                    setValue(value);
                }}
                onBlur={() => {
                    doValidation();
                }}
                onFocus={() => {
                    setFieldHasHadFocusAtLeastOnce(true);
                }}
                errorText={errorMessage}
                isValid={isValid}
                id={elementId}
                key={elementId}
                label={placeholder}
                required={isRequiredField}
                aria-label={caption}
                data-testid="contact-email"
            />
        </>
    );
};

ContactEmail.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as ContactEmailType;
    const { summaryCaption } = element;
    const value = getValue(formConfig, formValues, elementId);
    if (value === undefined) {
        return <></>;
    }

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={value} />;
};

ContactEmail.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    caption: string
): ClaimFormSubmitFieldValue | undefined => {
    // This contact information is handled in a separate object in the payload Stakeholder and Actions
    return undefined;
};

ContactEmail.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    const value = formValues[elementId];
    return value;
};

ContactEmail.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = ContactEmail.getValue(formValues, elementId);

    if (value === undefined) {
        return getPrimaryEmail();
    }
    return value;
};

ContactEmail.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    return getIsValid(formConfig, formValues, elementId);
};

ContactEmail.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasHadFocusAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHasHadFocusAtLeastOnce) {
        return undefined;
    }
    const isValid = ContactEmail.isValid(formConfig, formValues, elementId);
    return isValid;
};

ContactEmail.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const value = ContactEmail.getValue(formValues, elementId);
    return value;
};

export default ContactEmail;
